import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiPhone, FiPlay } = FiIcons;

const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80)'
        }}
      />
      
      {/* Dark Overlay for Better Contrast */}
      <div className="absolute inset-0 bg-black/70" />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/40 via-black/60 to-orange-900/40" />
      
      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="backdrop-blur-xl bg-black/60 rounded-3xl p-8 sm:p-12 border border-orange-500/30 shadow-2xl"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left Column: Text Content */}
            <div className="text-left">
              <motion.h1 
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Experience Travel, <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-pink-400 to-yellow-400">Redefined</span>
              </motion.h1>
              
              <motion.h2 
                className="text-xl sm:text-2xl text-orange-200 mb-4 font-light leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Luxury & Personalized Escapes for Canadians
              </motion.h2>
              
              <motion.p 
                className="text-lg text-gray-200 mb-6 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                Bespoke journeys to Mexico, the Caribbean, and Europe—crafted just for you by your dedicated travel experts. Enjoy exclusive perks, 24/7 support, and complete peace of mind.
              </motion.p>
              
              <motion.div 
                className="flex flex-col sm:flex-row gap-4 mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <motion.a
                  href="https://seetaluxuryescape.pathfndr.io/search/trips#travel"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(251, 146, 60, 0.4)" }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-6 py-3 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center gap-3 justify-center"
                >
                  Get My Free Custom Itinerary
                  <SafeIcon icon={FiArrowRight} className="text-xl" />
                </motion.a>
              </motion.div>
              
              <motion.a
                href="https://magical-kashata-aac293.netlify.app/#/contact"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(255, 255, 255, 0.2)" }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.0 }}
                className="bg-white/20 backdrop-blur-lg text-white px-6 py-3 rounded-full font-medium text-lg border border-white/40 hover:bg-white/30 transition-all duration-300 flex items-center gap-3 justify-center sm:inline-flex"
              >
                <SafeIcon icon={FiPhone} className="text-xl" />
                Book a Free Consultation
              </motion.a>
            </div>
            
            {/* Right Column: Video Iframe */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="relative rounded-2xl overflow-hidden border-2 border-orange-500/30 shadow-2xl"
            >
              <div className="aspect-w-16 aspect-h-9 w-full">
                <div style={{ position: 'relative', paddingTop: '56.25%', width: '100%' }}>
                  <iframe 
                    style={{ 
                      border: 'none', 
                      position: 'absolute', 
                      top: 0, 
                      width: '100%',
                      height: '100%',
                      borderRadius: '0.75rem'
                    }} 
                    src="https://my.onetake.ai/bb579be7/1a5825d5/" 
                    loading="lazy" 
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen" 
                    allowFullScreen={true}
                  />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/80 to-transparent p-4 text-white">
                <p className="text-lg font-medium">Experience the Seeta Signature Glow</p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;